// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider, Form, message, Modal, Popconfirm, Spin } from "antd";
import {
  EditOutlined,
  LoadingOutlined,
  QuestionCircleOutlined
} from "@ant-design/icons";
import { formItemBlockLayout } from "../../../utils/Constant";
import { SaveOutlined, StopOutlined } from "@ant-design/icons/lib";
import { Field } from "../../types/NewsTypes";
import { SettingsFormProps } from "../../types/SettingsTypes";
import {
  requestGetSettings,
  requestUpdateSettings
} from "../../../state/settings/SettingsEffects";
import {
  SettingsInfoResponseDTO,
  SettingsWriteRequestDTO
} from "../../../api/backend-openapi";
import SettingsInputFormSection from "./SettingsInputFormSection";
import { validate } from "../../../validator/SettingsValidator";
import { ValidatorResult } from "../../types/Types";
import { displayError } from "../../../utils/displayError";

const { confirm } = Modal;

const SettingsForm: React.FunctionComponent<SettingsFormProps> = (
  props: SettingsFormProps
) => {
  const { editMode, setEditMode, canEdit } = props;
  const { t } = useTranslation();

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [getResponseReceived, setGetResponseReceived] = useState<boolean>(
    false
  );

  const [settings, setSettings] = useState<SettingsInfoResponseDTO | null>(
    null
  );

  const [fields, setFields] = useState<Field[]>([]);

  const confirmationPopup = async () => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("global.confirmation"),
        content: t("settings.confirmation"),
        okText: t("global.yes"),
        cancelText: t("global.no"),
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    requestGetSettings({});
  }, []);

  useEffect(() => {
    return requestGetSettings.done.watch(({ result }) => {
      setGetResponseReceived(true);
      if (result.ok && result.data) {
        setSettings(result.data);
        setFields([
          {
            name: ["newsMainImageSize"],
            value: result.data.newsMainImageSize.toString()
          },
          {
            name: ["newsMainImageWidth"],
            value: result.data.newsMainImageWidth.toString()
          },
          {
            name: ["newsMainImageHeight"],
            value: result.data.newsMainImageHeight.toString()
          }
        ]);
      } else {
        displayError("settings.not_found");
      }
    });
  });

  useEffect(() => {
    return requestUpdateSettings.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        message.success(t("settings.success"));
        setEditMode(false);
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  const handleSubmit = (values: any) => {
    const settingsUpdate: SettingsWriteRequestDTO = {
      newsMainImageSize: values.newsMainImageSize,
      newsMainImageWidth: values.newsMainImageWidth,
      newsMainImageHeight: values.newsMainImageHeight
    };
    const validationResult: ValidatorResult = validate(settingsUpdate);
    if (!validationResult.validate) {
      displayError(validationResult.message);
    } else {
      confirmationPopup().then((confirmed: boolean) => {
        if (confirmed) {
          setButtonLoading(true);
          requestUpdateSettings(settingsUpdate);
        }
      });
    }
  };

  const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  return (
    <>
      {!getResponseReceived ? (
        <div style={{ textAlign: "center" }}>
          <Spin indicator={spinIcon} />
        </div>
      ) : settings ? (
        <Form {...formItemBlockLayout} onFinish={handleSubmit} fields={fields}>
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">{t("global.images")}</h3>
          </Divider>
          <SettingsInputFormSection editMode={editMode} />
          {canEdit && (
            <Divider orientation="left">
              <h3 className="text-secondary mb-0">{t("global.actions")}</h3>
            </Divider>
          )}
          {canEdit ? (
            editMode ? (
              <>
                <Form.Item className="text-center mb-0">
                  <Popconfirm
                    title={t("settings.edit.cancel_confirmation")}
                    okText={t("global.yes")}
                    cancelText={t("global.no")}
                    onConfirm={() => {
                      setEditMode(false);
                    }}
                    placement="top"
                    icon={<QuestionCircleOutlined />}
                    className="m-2"
                  >
                    <Button htmlType="reset">
                      <StopOutlined /> {t("global.cancel")}
                    </Button>
                  </Popconfirm>
                  <Button
                    className="btn-primary m-2"
                    htmlType="submit"
                    loading={buttonLoading}
                  >
                    <SaveOutlined /> {t("global.save")}
                  </Button>
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item className="text-center mb-0">
                  <Button
                    className="btn-primary m-2"
                    onClick={() => setEditMode(true)}
                  >
                    <EditOutlined /> {t("global.edit")}
                  </Button>
                </Form.Item>
              </>
            )
          ) : null}
        </Form>
      ) : (
        <div>{t("settings.not_found")}</div>
      )}
    </>
  );
};
export default SettingsForm;
